import React from 'react';
import { Link } from 'react-router-dom';
import { statePrivacyLaws } from '../../data/statePrivacyLaws';
import { BulletList, NumberedSection, PRIVACY_EMAIL, Paragraph, SubHeading, Term, policyLinkClass } from './PolicyBlocks';

/**
 * Sections 10–13 of the Privacy Policy: U.S. state privacy rights, the
 * California and Nevada supplements, and opt-out preference signals.
 * Section numbers are passed in so the parent owns the numbering and table of
 * contents.
 */
export function StatePrivacySections({
  contactSection
}: {
  contactSection: number;
}) {
  const emailLink = <a href={`mailto:${PRIVACY_EMAIL}`} className={policyLinkClass} data-id="element-685" data-mp="1tdh18v3ltsap">
      {PRIVACY_EMAIL}
    </a>;
  const optOutLink = <Link to="/do-not-sell-my-info" className={policyLinkClass} data-id="element-686" data-mp="1ol5y7jdkhqdx">
      Do Not Sell or Share My Personal Information
    </Link>;
  return <>
      {/* Section 10 — U.S. state privacy rights */}
      <NumberedSection id="section-10" number={10} title="Your U.S. State Privacy Rights" data-id="element-687" data-mp="vws2xbdv5862">
        <Paragraph data-id="element-688" data-mp="1anmp2g7dl32g">
          Residents of the states listed below have specific rights over their
          personal information under their state's consumer privacy law. We
          extend the rights described in this section to every U.S. visitor,
          regardless of where you live, and honor them to the extent required
          by the law of your state.
        </Paragraph>

        <SubHeading id="state-sale" data-id="element-689" data-mp="rqlgklem4ktr">
          Whether we "sell" or "share" personal information
        </SubHeading>
        <Paragraph data-id="element-690" data-mp="wq7uyy66g745">
          When you ask to be matched with schools, we disclose the information
          you submit — your name, contact details, location, and education
          interests — to educational institutions and marketing partners, and
          we may receive compensation for doing so. We also allow advertising
          partners to collect device and browsing information through cookies
          and pixels to show you ads on other websites. Under many state laws,
          these disclosures are a <strong className="text-navy" data-id="element-691" data-mp="i11ixlznh06x">"sale"</strong>{' '}
          of personal information and the use of it for{' '}
          <strong className="text-navy" data-id="element-692" data-mp="r0ewoqut4axg">"targeted advertising"</strong> (in
          California, <strong className="text-navy" data-id="element-693" data-mp="m0sh4ariailh">"sharing"</strong>). You
          have the right to opt out of both, as described below.
        </Paragraph>
        <Paragraph data-id="element-694" data-mp="3uhevt26qdb92">
          We do not use your personal information for profiling in furtherance
          of decisions that produce legal or similarly significant effects, and
          we do not knowingly sell or share the personal information of anyone
          under 18.
        </Paragraph>

        <SubHeading id="state-rights" data-id="element-695" data-mp="39j07gfeorjv">Your rights</SubHeading>
        <Paragraph data-id="element-696" data-mp="3bpm55ef4w24i">Depending on your state, you have the right to:</Paragraph>
        <BulletList items={[<Term key="a" label="Know and access" data-id="element-698" data-mp="36pzth9eyetxt">
              Confirm whether we process your personal information and obtain
              a copy of it.
            </Term>, <Term key="b" label="Correct" data-id="element-699" data-mp="1r08mqagetszc">
              Ask us to correct inaccurate personal information, taking into
              account its nature and the purposes of processing.
            </Term>, <Term key="c" label="Delete" data-id="element-700" data-mp="1i6vmeng0np38">
              Ask us to delete personal information you provided or that we
              obtained about you, subject to legal exceptions.
            </Term>, <Term key="d" label="Data portability" data-id="element-701" data-mp="29z2z5cseihcc">
              Receive your personal information in a portable, readily usable
              format you can transfer to another company.
            </Term>, <Term key="e" label="Opt out of sale" data-id="element-702" data-mp="20x3hci9kb98c">
              Direct us to stop selling your personal information.
            </Term>, <Term key="f" label="Opt out of targeted advertising" data-id="element-703" data-mp="kibqwavz8c1o">
              Direct us to stop using or disclosing your personal information
              to show you ads based on your activity across other websites.
            </Term>, <Term key="g" label="Opt out of profiling" data-id="element-704" data-mp="101wg7r450ax8">
              Opt out of automated profiling used to make decisions that
              produce legal or similarly significant effects. As noted above,
              we do not engage in this processing.
            </Term>, <Term key="h" label="Know who receives your data" data-id="element-705" data-mp="qz57w06k5v9t">
              Where your state provides it (for example Delaware, Maryland,
              Minnesota, Oregon, and Rhode Island), obtain a list of the
              categories of third parties, or the specific third parties, to
              which we have disclosed your personal information.
            </Term>, <Term key="i" label="Appeal" data-id="element-706" data-mp="19r7q3fyuljm6">
              Appeal our decision if we decline to act on your request.
            </Term>, <Term key="j" label="Non-discrimination" data-id="element-707" data-mp="3uhoqsc3cjory">
              Exercise any of these rights without being denied services,
              charged a different price, or receiving a different quality of
              service.
            </Term>]} data-id="element-697" data-mp="36r145cs25ef0" />
        <Paragraph data-id="element-708" data-mp="3pd4dddix1r1l">
          Not every right is available in every state. The table below lists
          each state law, when it took effect, and any differences from the
          rights above.
        </Paragraph>

        <SubHeading id="state-how" data-id="element-709" data-mp="2z37bbtvogje">How to exercise your rights</SubHeading>
        <Paragraph data-id="element-710" data-mp="6ylqa2252hjl">You can submit a request in any of these ways:</Paragraph>
        <BulletList items={[<Term key="a" label="Opt out of sale and targeted advertising" data-id="element-712" data-mp="bzrje0zpo6tn">
              Use our {optOutLink} page. This takes effect for the information
              you enter on that page.
            </Term>, <Term key="b" label="Browser signal" data-id="element-713" data-mp="2ioebdz1liead">
              Turn on the Global Privacy Control (GPC) in a supported browser
              or extension. We treat it as a request to opt out of sale and
              targeted advertising for that browser (see Section 13).
            </Term>, <Term key="c" label="Email" data-id="element-714" data-mp="2anq4pma42q9i">
              Write to {emailLink} with the subject line{' '}
              <strong className="text-navy" data-id="element-715" data-mp="2pkzbnobwe2mc">"Privacy Rights Request"</strong>.
              Tell us your state of residence and which right you want to
              exercise, and include the name, email address, and phone number
              you used on our site so we can locate your records.
            </Term>, <Term key="d" label="Mail" data-id="element-716" data-mp="2tn426e71uj4r">
              Send a written request to Ironsmith Media, Attn: Privacy, 1612
              UPAS Street, San Diego, CA 92103.
            </Term>]} data-id="element-711" data-mp="bwn7sijx28sy" />
        <Paragraph data-id="element-717" data-mp="33buf2l3mnrij">
          You do not need to create an account to submit a request. Requests
          are free of charge, up to twice in any 12-month period; we may
          charge a reasonable fee or decline requests that are manifestly
          unfounded, excessive, or repetitive, and will explain why.
        </Paragraph>

        <SubHeading id="state-verify" data-id="element-718" data-mp="3vzdiotdw9jhs">Verifying your request</SubHeading>
        <Paragraph data-id="element-719" data-mp="4ssv5jn1stpi">
          To protect your information, we verify requests to know, access,
          correct, or delete by matching the details you provide against the
          records we hold, and we may contact you at the email address or
          phone number on file to confirm. We use the information you provide
          for verification only for that purpose. Opt-out requests do not
          require verification.
        </Paragraph>

        <SubHeading id="state-agent" data-id="element-720" data-mp="3i0xepa7f273c">Authorized agents</SubHeading>
        <Paragraph data-id="element-721" data-mp="348zfbr3ibpkd">
          You may use an authorized agent to submit a request on your behalf.
          We will ask the agent for signed, written permission from you (or a
          valid power of attorney), and may ask you to verify your identity
          directly with us. Parents or legal guardians may submit requests on
          behalf of a minor.
        </Paragraph>

        <SubHeading id="state-timing" data-id="element-722" data-mp="3equpbfhewom1">When we respond</SubHeading>
        <Paragraph data-id="element-723" data-mp="39ri40z66vplk">
          We confirm receipt of requests within 10 business days and respond
          within the period your state requires — 45 days in most states, and
          90 days in Iowa. Where the law permits, we may extend that period
          once, by up to 45 additional days, when reasonably necessary; if we
          do, we will tell you within the original period and explain why. We
          act on opt-out requests as soon as feasible and no later than 15
          business days after receipt.
        </Paragraph>

        <SubHeading id="state-appeal" data-id="element-724" data-mp="2m2yukgbel6rw">How to appeal a decision</SubHeading>
        <Paragraph data-id="element-725" data-mp="2i0vjhssnc475">
          If we decline to take action on your request, we will tell you why.
          You may appeal by emailing {emailLink} with the subject line{' '}
          <strong className="text-navy" data-id="element-726" data-mp="282617ywu8hqd">"Privacy Request Appeal"</strong> within
          a reasonable time after receiving our decision, and include the
          original request. We will respond in writing within the period your
          state requires, generally 45 to 60 days, explaining any action
          taken and the reasons for our decision. If your appeal is denied,
          you may contact the regulator for your state listed in the table
          below.
        </Paragraph>

        <SubHeading id="state-table" data-id="element-727" data-mp="11hk2jd1hzwt7">State-by-state summary</SubHeading>
        <div className="-mx-4 sm:mx-0 overflow-x-auto" data-id="element-728" data-mp="4v6xxmkooqdy">
          <table className="min-w-[720px] w-full text-left text-sm border-collapse" data-id="element-729" data-mp="9s78a9okgfav">
            <caption className="sr-only" data-id="element-730" data-mp="dukcuurku762">
              U.S. state consumer privacy laws and how they apply
            </caption>
            <thead data-id="element-731" data-mp="im2c9edybzox">
              <tr className="border-b-2 border-navy/20 text-navy" data-id="element-732" data-mp="imljo15z75op">
                <th scope="col" className="py-3 pl-4 sm:pl-0 pr-4 font-bold" data-id="element-733" data-mp="3i9bpugkykkfc">
                  State and law
                </th>
                <th scope="col" className="py-3 pr-4 font-bold whitespace-nowrap" data-id="element-734" data-mp="3m8kl5pvncqd0">
                  Response time
                </th>
                <th scope="col" className="py-3 pr-4 font-bold" data-id="element-735" data-mp="225cs1z4kz65v">
                  Appeal
                </th>
                <th scope="col" className="py-3 pr-4 font-bold" data-id="element-736" data-mp="2677vpa9zhvmt">
                  Differences and regulator
                </th>
              </tr>
            </thead>
            <tbody data-id="element-737" data-mp="2b3yj6io4bti9">
              {statePrivacyLaws.map(law => <tr key={law.state} className="border-b border-gray-200 align-top" data-id="element-738" data-mp="1eh3axaibb6l2">
                  <th scope="row" className="py-3 pl-4 sm:pl-0 pr-4 font-normal" data-id="element-739" data-mp="1iif5lug1mi04">
                    <span className="block font-bold text-navy" data-id="element-740" data-mp="1nfyhwot6kkpf">{law.state}</span>
                    <span className="block text-gray-600 mt-0.5" data-id="element-741" data-mp="1s76vzy5ggu8x">{law.law}</span>
                    <span className="block text-gray-500 text-xs mt-1" data-id="element-742" data-mp="1s91fd812weaw">
                      Effective {law.effective}
                    </span>
                  </th>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap" data-id="element-743" data-mp="zpb981bfxr1t">
                    {law.responseDays} days
                  </td>
                  <td className="py-3 pr-4 text-gray-700" data-id="element-744" data-mp="n8a7jcydo481">
                    {law.appeal ? 'Yes' : 'No'}
                  </td>
                  <td className="py-3 pr-4 text-gray-700" data-id="element-745" data-mp="i8fh1aiilgni">
                    {law.notes !== '—' && <span className="block" data-id="element-746" data-mp="e7vhy1eds2yq">{law.notes}</span>}
                    <span className="block text-gray-500 text-xs mt-1" data-id="element-747" data-mp="e9oir02bbsyt">
                      {law.regulator}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <Paragraph data-id="element-748" data-mp="3vnk3e3bocd7d">
          Residents of Washington and Nevada have additional protections for
          consumer health data. We do not collect consumer health data.
        </Paragraph>
      </NumberedSection>

      {/* Section 11 — California supplement */}
      <NumberedSection id="section-11" number={11} title="Additional Information for California Residents (CCPA/CPRA)" data-id="element-749" data-mp="3mp87bls1gn7j">
        <Paragraph data-id="element-750" data-mp="37zn9hui11jc6">
          This section supplements Section 10 for California residents, as
          required by the California Consumer Privacy Act as amended by the
          California Privacy Rights Act.
        </Paragraph>

        <SubHeading data-id="element-751" data-mp="2y4dbriynvy47">Categories of personal information collected</SubHeading>
        <Paragraph data-id="element-752" data-mp="332erpv0sjhbg">
          In the preceding 12 months, we collected the following categories of
          personal information, from you directly and automatically from your
          device, for the business purposes described in Section 2:
        </Paragraph>
        <BulletList items={['Identifiers (e.g., name, email address, phone number, postal address, IP address, online identifiers)', 'Personal information described in Cal. Civ. Code § 1798.80(e) (e.g., contact information)', 'Internet or other electronic network activity (e.g., pages viewed, interactions with our website and ads)', 'Geolocation data at the ZIP code or IP-address level (not precise geolocation)', 'Education and program-interest information (e.g., highest level of education, graduation year, degree level and area of study)', 'Inferences drawn from the above (e.g., program preferences)']} data-id="element-753" data-mp="3h1wk0kdapcpk" />

        <SubHeading data-id="element-754" data-mp="27tkx1c8ns99y">Sale, sharing, and disclosure</SubHeading>
        <Paragraph data-id="element-755" data-mp="1k2xnkmj3hbla">
          In the preceding 12 months, we sold identifiers, § 1798.80(e)
          information, education and program-interest information, and
          inferences to educational institutions and marketing partners, and
          shared identifiers and internet activity with advertising partners
          for cross-context behavioral advertising. We disclosed all of the
          categories above to service providers for business purposes, such as
          hosting, analytics, and consent recordkeeping.
        </Paragraph>

        <SubHeading data-id="element-756" data-mp="2ziwgwro1z31h">Sensitive personal information</SubHeading>
        <Paragraph data-id="element-757" data-mp="3uu4f5flnabsh">
          We do not collect or use sensitive personal information for the
          purpose of inferring characteristics about you, so the right to
          limit its use does not apply.
        </Paragraph>

        <SubHeading data-id="element-758" data-mp="8lmpcqwsov8a">Retention</SubHeading>
        <Paragraph data-id="element-759" data-mp="8p0pdshzksck">
          We retain inquiry and contact information for up to 36 months after
          your last interaction with us, consent records (including the
          disclosure you agreed to, timestamp, IP address, and page URL) for
          up to 5 years to demonstrate compliance with telemarketing laws, and
          device and browsing data for up to 13 months, unless a longer period
          is required by law.
        </Paragraph>

        <SubHeading data-id="element-760" data-mp="wb79hd4opyj5">Financial incentives</SubHeading>
        <Paragraph data-id="element-761" data-mp="11bbpl21okr8u">
          We do not offer financial incentives or price differences in
          exchange for your personal information.
        </Paragraph>

        <SubHeading data-id="element-762" data-mp="15dge5md9uxom">"Shine the Light" (Cal. Civ. Code § 1798.83)</SubHeading>
        <Paragraph data-id="element-763" data-mp="2pw0ov6zgn2y4">
          California residents may request, once per calendar year, a list of
          the categories of personal information we disclosed to third parties
          for their direct marketing purposes in the prior calendar year and
          the names and addresses of those third parties. Email {emailLink}{' '}
          with the subject line{' '}
          <strong className="text-navy" data-id="element-764" data-mp="39ioy6oo1xefl">"Shine the Light Request"</strong>.
        </Paragraph>
      </NumberedSection>

      {/* Section 12 — Nevada */}
      <NumberedSection id="section-12" number={12} title="Additional Information for Nevada Residents" data-id="element-765" data-mp="34knga72d3l5p">
        <Paragraph data-id="element-766" data-mp="2uu27xrwvz29l">
          Under Nevada Revised Statutes Chapter 603A, Nevada residents may
          direct us not to sell covered information we have collected or will
          collect about them. Submit your request through our {optOutLink}{' '}
          page, or by emailing {emailLink} with the subject line{' '}
          <strong className="text-navy" data-id="element-767" data-mp="1lkxlm3vmeblv">"Nevada Opt-Out Request"</strong>. We
          will respond within 60 days, which we may extend once by up to 30
          days with notice.
        </Paragraph>
      </NumberedSection>

      {/* Section 13 — opt-out preference signals */}
      <NumberedSection id="section-13" number={13} title="Opt-Out Preference Signals and Do-Not-Track" data-id="element-768" data-mp="1gu7i6w6r8itt">
        <Paragraph data-id="element-769" data-mp="11w5feo7js1dm">
          <strong className="text-navy" data-id="element-770" data-mp="11vvkqnbk561l">Global Privacy Control.</strong> We
          recognize the Global Privacy Control (GPC) signal. When we detect it,
          we treat it as a valid request to opt out of the sale of your
          personal information and its use for targeted advertising, for that
          browser or device and any profile we associate with it. Several
          states — including California, Colorado, Connecticut, Delaware,
          Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey,
          Oregon, and Texas — require businesses to honor these signals. You
          can learn more and enable GPC at{' '}
          <a href="https://globalprivacycontrol.org" target="_blank" rel="noopener noreferrer" className={policyLinkClass} data-id="element-771" data-mp="2xezvtpj59fso">
            globalprivacycontrol.org
          </a>
          .
        </Paragraph>
        <Paragraph data-id="element-772" data-mp="36dbm1qg3mbkl">
          <strong className="text-navy" data-id="element-773" data-mp="3l3sn38otmzgy">Do-Not-Track.</strong> Some browsers
          offer a Do-Not-Track ("DNT") setting. Because no uniform standard
          for DNT has been adopted, we do not respond to DNT signals, but we
          do honor GPC as described above.
        </Paragraph>
        <Paragraph data-id="element-774" data-mp="3q35hcei0v8tk">
          If you have questions about any of these rights, contact us using
          the information in Section {contactSection}.
        </Paragraph>
      </NumberedSection>
    </>;
}